import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Brand from "@/components/Brand";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Map an axios/login error to friendly copy. Backend returns:
 *  - 401 "Invalid email or password"
 *  - 403 "This account is deactivated. Contact the studio."
 *  - 403 "Your profile hasn't been claimed yet…"
 * Anything else (network failure / 5xx) shows a generic but informative
 * fallback rather than the dreaded "Something went wrong".
 */
function describeLoginError(err) {
  const status = err?.response?.status;
  const raw = err?.response?.data?.detail;
  const detail = typeof raw === "string" ? raw : "";
  if (status === 401) return "Invalid email or password.";
  if (status === 403) {
    if (/deactivat/i.test(detail)) return "This account is not active. Contact the studio.";
    if (/claim/i.test(detail)) return detail; // already friendly
    return detail || "Access denied.";
  }
  if (status && status >= 500) return "Server error — please try again in a moment.";
  if (err?.message === "Network Error" || !err?.response) {
    return "Network error — check your connection and try again.";
  }
  return detail || "Could not sign in. Please try again.";
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const next = useMemo(() => new URLSearchParams(location.search).get("next") || "", [location.search]);
  const adminIntent = next.startsWith("/admin");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const u = await login(email, password);
      // Decide destination
      if (adminIntent && u.role !== "admin") {
        toast.error("You do not have admin access. Redirecting to your client portal.");
        navigate("/dashboard", { replace: true });
      } else {
        toast.success("Welcome back");
        // Admin always goes to /admin (even if they typed /login directly).
        // Otherwise honor `next` for the same role, else the role default.
        const dest = u.role === "admin"
          ? "/admin"
          : (next && !next.startsWith("/admin") ? next : "/dashboard");
        navigate(dest, { replace: true });
      }
    } catch (e2) {
      setErr(describeLoginError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#141C16] px-6 py-16">
      <div className="w-full max-w-md">
        <div className="text-center mb-12">
          <Link to="/" data-testid="login-brand-link">
            <Brand variant="monogram" className="h-32 w-32 mx-auto" />
          </Link>
        </div>
        <div className="frame-card p-8 lg:p-12">
          <div className="mb-8">
            <span className="overline text-[#D2A853]" data-testid="login-overline">
              {adminIntent ? "Studio Admin" : "Sign In"}
            </span>
            <h2 className="font-display text-3xl text-[#E8E9E3] mt-2" data-testid="login-heading">
              {adminIntent ? "Admin Sign In" : "Returning Client"}
            </h2>
            {adminIntent && (
              <p className="text-sm text-[#A3A8A4] mt-3" data-testid="login-admin-subtle">
                Restricted access. Studio staff only.
              </p>
            )}
          </div>
          <form onSubmit={submit} className="space-y-6">
            <div>
              <label className="overline text-[#A3A8A4] block mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-line"
                placeholder={adminIntent ? "admin@paiddewz.com" : "you@example.com"}
                data-testid="login-email-input"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="overline text-[#A3A8A4] block mb-2">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-line"
                placeholder="••••••••"
                data-testid="login-password-input"
                autoComplete="current-password"
              />
            </div>
            {err && <div className="text-[#CF6679] text-sm" data-testid="login-error">{err}</div>}
            <button
              type="submit"
              disabled={busy}
              className="btn-brass w-full flex items-center justify-center gap-2"
              data-testid="login-submit-btn"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {adminIntent ? "Enter Admin Console" : "Sign In"}
            </button>
          </form>
          {!adminIntent && (
            <div className="mt-8 pt-6 border-t border-[#D2A853]/20 text-center">
              <span className="text-sm text-[#A3A8A4]">First time? </span>
              <Link to="/intake" className="text-sm text-[#D2A853] hover:text-[#E2BC6A]" data-testid="login-go-to-intake">
                Submit a new consultation →
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
